import axiosStatic from 'axios';
import { Dictionary, keyBy } from 'lodash';
import { Optional } from 'utility-types';
import { AdvancedSetting } from 'api/advanced-settings/settings-types';
import { Subscription } from 'api/billing/types';
import { AuditLog, CurrencyAuditLogMeta } from 'api/financials-audit-logs/type';
import { HolidayCalendar } from 'api/holiday-calendar/types';
import { HttpClient } from 'api/http-client';
import { ProjectPermission } from 'api/permissions';
import { PersonalTask } from 'api/personal-tasks';
import { AccountVisibility } from 'api/visibility';
import RocketlaneLogo from 'assets/rocketlane_logo_64px.png';
import {
  API_HOST_FOR_MOBILE_META,
  API_NAMESPACE,
  API_VERSION,
} from 'config/environment';
import { InitWorkerRequestType, InitWorkerApi } from 'initWorker';
import { PermissionSet } from 'stores/permission-sets';
import { Company, DateType, User } from '../models';
import { ProjectInUserProfile, UserProfileInfo } from '../user';

declare global {
  interface Window {
    initWorker: Worker;
  }
}
const customDomainApi = '/accounts/custom-domain';

export enum TimesheetAccessState {
  Enabled = 'ENABLED',
  OnlyApproval = 'ONLY_APPROVAL',
  Disabled = 'DISABLED',
}

export interface BrandTheme {
  sideNavBg: string;
  sideNavText: string;
  brandColor: string;
  brandHoverColor: string;
  brandColorInRgb: string;
}

interface MetaData {
  theme: BrandTheme;
  showProjectPlanBackground?: boolean;
  ftue?: boolean;
  canNotificationsConfigBeEdited?: boolean;
  userCount?: number;
}

export enum AccountStatus {
  Trial = 'TRIAL',
  Active = 'ACTIVE',
  Grace = 'GRACE',
  Suspended = 'SUSPENDED',
  /**
   * @deprecated. Make use of `Active` status instead.
   */
  Paid = 'PAID',
}

export enum WarningErrorCode {
  PaymentFailed = 'PAYMENT_FAILED',
}

export enum WarningType {
  Billing = 'BILLING',
}

export type Warning = {
  errorCode: WarningErrorCode;
  warningType: WarningType;
};

export interface Account {
  accountName: string;
  accountId: number;
  company: Company;
  metaData: Partial<MetaData>;
  primaryDomainName: string;
  defaultDomainName: string;
  expiryDate: string | null; // YYYY-MM-DD
  status: AccountStatus;
  timeZone: string;
  systemBotId: number;
  accountWarnings?: Warning[];
  createdAt: string | null;
}

export type UpdateAccountTimezonePayload = Pick<
  Account,
  'accountId' | 'timeZone'
>;

export type UpdateAccountPayload = {
  metaData?: MetaData;
  company?: Pick<Company, 'companyName'>;
};

export enum IdpType {
  Google = 'GOOGLE',
  Saml = 'SAML',
}

interface Idp {
  idpId: string;
  idpType: IdpType;
  enabled: boolean;
}

export interface OktaMetaData {
  clientId: string;
  issuerDomain: string;
  userName?: string;
  idps?: Idp[];
}

export interface AccountMeta {
  company: Company;
  okta: OktaMetaData;
  features: {
    timesheets: boolean;
    financials: boolean;
    disablePoweredBy: boolean;
    disableCustomerSlackConnect: boolean;
    hippaCompliance: boolean;
  };
}

export interface BillingMeta {
  subscription: Omit<Subscription, 'subscriptionMeta'>;
  accountAdmin: User;
}

export interface DkimHost {
  host: string;
  data: string;
  verified: boolean;
}

export interface EmailConfigBase {
  emailDomain: string;
  invitationPrefix: string;
  notificationPrefix: string;
  enabled?: boolean;
}

export interface EmailConfig extends EmailConfigBase {
  domainName: string;
  spf: DkimHost;
  dkim1: DkimHost;
  dkim2: DkimHost;
  updatedAt?: string;
  createdAt?: string;
  verified: boolean;
  data?: DkimHost[]; // after transforming
}

export interface FtuePayload {
  enabled: boolean;
}

export interface CustomDomainHost {
  cnameType: 'SSL' | 'Domain';
  host: string;
  value: string;
}

export interface CustomDomain {
  domainName: string;
  cnames: CustomDomainHost[];
  primary: boolean;
  deleted: boolean;
}

export interface NotificationData {
  pendingNotifications?: number;
  newNotificationAvailable: boolean;
}

export interface AccountInitialData {
  accountMeta: AccountMeta;
  account: Account;
  accountConfig: AdvancedSetting[];
  userProfile: UserProfileInfo;
  companies: Company[];
  permissionSetList: PermissionSet[];
  accountVisibilitySettings: AccountVisibility;
  newNotificationAvailable: boolean;
  personalTasks: PersonalTask[];
  timeSheetAccess: TimesheetAccessState;
  billingMeta: BillingMeta;
  holidayCalendar: HolidayCalendar[];
  showG2: boolean;
  doesSprintsExist: boolean;
}

export interface AccountInitDataProjectsQuery {
  projects: ProjectInUserProfile[];
  byId: Dictionary<ProjectInUserProfile>;
}

export interface RawAccountInitialData
  extends Omit<AccountInitialData, 'userProfile'> {
  userProfile: Omit<AccountInitialData['userProfile'], 'permissions'> & {
    permissions: Omit<
      AccountInitialData['userProfile']['permissions'],
      'projects'
    > & { projects: ProjectPermission[] };
  };
}

export interface CreateCustomDomain {
  customDomainName: string;
}

export interface AccountSignUpPayload {
  emailId: string;
  companyName: string;
  domainName: string;
  firstName: string;
  lastName: string;
  newPassword: string;
  token: string;
  contactNumber: string;
  timeZone: string;
}

interface Session {
  sessionToken: string;
  sessionUser: User;
}
interface AccountWithDomain extends Account {
  primaryDomainName: string;
  defaultDomainName: string;
  expiryDate: string;
}
export interface AccountSignUpResponse {
  session: Session;
  account: AccountWithDomain;
  templateId: number;
}

export type IsDomainExistsPayload = {
  domainName: string;
  token: string;
  throughGoogleAuth: boolean;
};
export interface IsDomainExistsResponse extends IsDomainExistsPayload {
  domainExists: boolean;
}

export enum RequestPlanType {
  Buy = 'BUY',
  TrialExtend = 'TRIAL_EXTEND',
}

type ForgotAccountPayload = {
  email: string;
};

export type RequestPlanPayload = {
  type: RequestPlanType;
  metaData?: {
    plan: 'PREMIUM';
    noOfAgents: number;
  };
};

export interface Currency {
  currencyName: string;
  currencyCode: string;
  currencySymbol: string;
}

export interface AccountSignUpThroughGooglePayload {
  emailId: string;
  companyName: string;
  domainName: string;
  firstName: string;
  lastName: string;
  newPassword: string;
  contactNumber: string;
  timeZone: string;
  signUpHandlerType: string;
  credential: string;
}

export type CurrencyList = Currency[];

export interface CurrencyMapping {
  accountCurrencyMappingId: number;
  currency: Currency;
  exchangeRate: number;
  defaultCurrency: boolean;
  enabled: boolean;
  activeDefaultCurrency?: Currency;
  updatedAt?: DateType;
  updatedBy?: number;
  createdAt?: DateType;
  createdBy?: number;
  backdatedTo?: DateType;
}

export type CurrencyMappingList = CurrencyMapping[];

export type UpdateCurrencyMappingPayload = Pick<
  CurrencyMapping,
  'accountCurrencyMappingId' | 'currency'
> &
  Optional<
    Pick<CurrencyMapping, 'enabled' | 'exchangeRate' | 'defaultCurrency'>,
    'enabled' | 'exchangeRate' | 'defaultCurrency'
  > & {
    backdate?: DateType;
  };

class AccountHttpClient extends HttpClient {
  public async getAccountDetails(): Promise<Account> {
    return this.get('/accounts/profile');
  }

  public getCurrencyList = async (): Promise<Currency[]> =>
    this.get('/accounts/currencies');

  public getCurrencyMappingList = async (): Promise<CurrencyMappingList> =>
    this.get('/accounts/currency-mapping');

  public addCurrencyMapping = async (payload: CurrencyMapping): Promise<void> =>
    this.post('/accounts/currency-mapping', payload);

  public async updateCurrencyMapping(
    payload: UpdateCurrencyMappingPayload,
  ): Promise<void> {
    return this.put(
      `/accounts/currency-mapping${
        payload.backdate != null ? `?backdate=${payload.backdate}` : ''
      }`,
      payload,
    );
  }

  public getCurrencyMappingHistory = async (
    currencyCode: Currency['currencyCode'],
  ): Promise<CurrencyMappingList> =>
    this.get(`/accounts/currency-mapping/history/${currencyCode}`);

  public getCurrencyMappingAuditLog = async (
    currencyCode: Currency['currencyCode'],
  ): Promise<AuditLog<CurrencyAuditLogMeta>[]> =>
    this.get(`/accounts/currency-mapping/audit-log/${currencyCode}`);

  public async updateAccount(data: UpdateAccountPayload): Promise<Account> {
    return this.put('/accounts/profile', data);
  }

  public async updateTimezone(
    data: UpdateAccountTimezonePayload,
  ): Promise<Account> {
    return this.put('/accounts/profile', data);
  }

  public async getMeta(
    domain: string | null,
    pathname: string | null,
  ): Promise<AccountMeta> {
    // if in localstorage, return that
    // else fetch from api and store in localstorage
    if (localStorage.getItem('accountMeta')) {
      return new Promise((resolve) => {
        const meta = localStorage.getItem('accountMeta');

        if (meta) {
          resolve(JSON.parse(meta));
        }
      });
    }

    if (
      navigator.userAgent.includes('Mobile') &&
      !pathname?.startsWith('/forms')
    ) {
      return axiosStatic
        .get<AccountMeta>(
          `${API_HOST_FOR_MOBILE_META}/${API_NAMESPACE}/${API_VERSION}/accounts/get-mobile-meta?domain=${domain}`,
          {
            headers: null,
          },
        )
        .then((x) => x.data)
        .then((response: AccountMeta): AccountMeta => {
          const { company } = response;
          const { companyLogoUrl } = company;

          return {
            ...response,
            company: {
              ...company,
              companyLogoUrl,
            },
            features: response.features ?? {},
          };
        });
    }

    let response: AccountMeta;

    try {
      response = (await InitWorkerApi.getData(
        InitWorkerRequestType.GetMeta,
      )) as AccountMeta;
    } catch {
      response = await this.get<AccountMeta>('/accounts/get-meta', {
        headers: null,
      });
    }

    const { company } = response;
    const companyLogoUrl = company.companyLogoUrl || RocketlaneLogo;

    const data = {
      ...response,
      company: {
        ...company,
        companyLogoUrl,
      },
      features: response.features ?? {},
    };

    return data;
  }

  public async getEmailConfig(): Promise<EmailConfig[]> {
    return this.get('/email/config');
  }

  public async addEmailConfig(
    data: Pick<EmailConfigBase, 'emailDomain' | 'enabled'>,
  ): Promise<EmailConfig> {
    return this.post('/email/config', data);
  }

  public async updateEmailConfig(
    data: AtLeast<EmailConfigBase, 'emailDomain'>,
  ): Promise<EmailConfig> {
    return this.put('/email/config', data);
  }

  public async verifyEmailConfig(
    data: Pick<EmailConfigBase, 'emailDomain'>,
  ): Promise<EmailConfig> {
    return this.post('/email/verify', data);
  }

  public async toggleFTUE(payload: FtuePayload) {
    return this.put('/accounts/ftue', payload);
  }

  public getCustomDomain = (): Promise<CustomDomain[]> =>
    this.get<unknown, CustomDomain[]>(customDomainApi);

  public createCustomDomain = (
    payload: CreateCustomDomain,
  ): Promise<CustomDomain[]> =>
    this.post<CustomDomain[], CreateCustomDomain>(customDomainApi, payload);

  public verifyCustomDomain = (payload: CreateCustomDomain): Promise<boolean> =>
    this.post<boolean, CreateCustomDomain>(
      `${customDomainApi}/verify`,
      payload,
    );

  public getInitData = async (): Promise<AccountInitialData> => {
    let data: RawAccountInitialData;

    try {
      data = (await InitWorkerApi.getData(
        InitWorkerRequestType.Init,
      )) as RawAccountInitialData;
    } catch {
      data = await this.get<RawAccountInitialData>('/home/init');
    }

    return {
      ...data,
      userProfile: {
        ...data.userProfile,
        permissions: {
          ...data.userProfile.permissions,
          projects: keyBy(data.userProfile.permissions.projects, 'projectId'),
        },
      },
    };
  };

  public signUp = ({
    token,
    ...payload
  }: AccountSignUpPayload): Promise<AccountSignUpResponse> => {
    return this.put('/accounts/sign-up', payload, { params: { token } });
  };

  public resendAccountActivation = () => {
    return this.post('/accounts/activate/resend');
  };

  public isDomainExists = ({
    token,
    ...payload
  }: IsDomainExistsPayload): Promise<IsDomainExistsResponse> => {
    if (payload.throughGoogleAuth) {
      return this.post(
        'https://admin.api.rocketlane.com/api/v1/accounts/domain',
        {
          domainName: payload.domainName,
        },
        {
          params: { token },
        },
      );
    }

    return this.post(
      '/accounts/domain',
      { domainName: payload.domainName },
      { params: { token } },
    );
  };

  public requestPlan = (payload: RequestPlanPayload) => {
    return this.put('/accounts/request-plan', payload);
  };

  public forgotAccount = (payload: ForgotAccountPayload) => {
    return this.post(
      'https://admin.api.rocketlane.com/api/v1/accounts/forgot',
      {
        emailId: payload.email,
      },
    );
  };

  public signUpThroughGoogle = ({
    ...payload
  }: AccountSignUpThroughGooglePayload): Promise<AccountSignUpResponse> => {
    return this.post(
      'https://admin.api.rocketlane.com/api/v1/accounts/signup-google',
      payload,
    );
  };

  public memberActivateResend = ({ email }: { email: string }) => {
    return this.post('/members/activate/resend', { email });
  };

  public logAccountMetricsToDataDog = (payload) =>
    this.post('/accounts/datadog/gauge', payload);
}

export const AccountAPI = new AccountHttpClient();
